import { useState } from "react";
import { jsPDF } from "jspdf";
import {
  FileDown,
  Loader2,
  Smartphone,
  Monitor,
  LogIn,
  ScanFace,
  Fingerprint,
  MapPin,
  Users,
  Timer,
  ClipboardCheck,
  FileBarChart,
  LayoutDashboard,
  DoorOpen,
  Info,
  Check,
  KeyRound,
  Zap,
  Building2,
  Coffee,
  Bell,
} from "lucide-react";
import { toast } from "sonner";

const TZ = "Asia/Muscat";
const muscatNow = () =>
  new Intl.DateTimeFormat("en-GB", {
    dateStyle: "long",
    timeStyle: "short",
    timeZone: TZ,
  }).format(new Date());
const muscatDate = () =>
  new Intl.DateTimeFormat("en-GB", { dateStyle: "long", timeZone: TZ }).format(new Date());

// ---- Manual content model (single source of truth for screen + PDF) ----
const MANUAL = [
  {
    key: "staff",
    audience: "Staff — Mobile App",
    icon: Smartphone,
    sections: [
      {
        key: "staff-login",
        title: "1. Signing In",
        icon: LogIn,
        blocks: [
          { type: "p", text: "The Smart-In mobile app is used only by staff to record their attendance. Staff members cannot sign into the web Admin Portal." },
          {
            type: "steps",
            items: [
              "Open the Smart-In app on your phone.",
              "Enter the Staff ID assigned to you by your administrator (for example, EMP001).",
              "Enter your 4-digit PIN.",
              "Tap Sign In. On your very first login you will be guided through Face (biometric) setup.",
            ],
          },
          { type: "mockup", screen: { screen: "Sign In", elements: [
            { type: "field", label: "Staff ID", value: "EMP001" },
            { type: "pin", label: "4-Digit PIN" },
            { type: "button", label: "Sign In" },
            { type: "caption", label: "Staff use the mobile app only" },
          ] } },
          { type: "note", text: "If you forget your PIN, your administrator can reset it from the User Management page of the Admin Portal." },
        ],
      },
      {
        key: "staff-biometric",
        title: "2. Biometric / Face Setup",
        icon: Fingerprint,
        blocks: [
          { type: "p", text: "On first sign-in, the app enrolls a reference photo of your face. This reference is used by the Claude AI engine to verify your identity every time you clock in." },
          {
            type: "steps",
            items: [
              "When prompted, allow the app to use your camera.",
              "Position your face inside the on-screen frame in good, even lighting.",
              "Hold still and capture the selfie.",
              "Confirm the photo — this becomes your enrolled reference face.",
            ],
          },
          { type: "mockup", screen: { screen: "Face Setup", elements: [
            { type: "camera", label: "Center your face in the frame" },
            { type: "button", label: "Capture Photo" },
            { type: "caption", label: "Good lighting - no mask or sunglasses" },
          ] } },
          { type: "note", text: "Use a clear, front-facing photo with no sunglasses or masks. A good reference photo means faster, more accurate clock-ins." },
        ],
      },
      {
        key: "staff-clock",
        title: "3. Clock-In / Clock-Out with Face Check",
        icon: ScanFace,
        blocks: [
          { type: "p", text: "Attendance is verified two ways at once: your GPS location must be inside your assigned site's geofence, and your live selfie must match your enrolled face." },
          {
            type: "steps",
            items: [
              "Make sure you are physically within your assigned site's radius.",
              "Open the app and tap Clock In.",
              "Allow location and camera access when prompted.",
              "The app captures a live selfie and your GPS position.",
              "Claude AI compares the selfie to your enrolled face. On a confident match, your Clock-In is recorded with the exact time (Asia / Muscat, GST +4).",
              "At the end of your duty, repeat the same steps and tap Clock Out.",
            ],
          },
          { type: "mockup", screen: { screen: "Clock In", elements: [
            { type: "camera", label: "Live selfie" },
            { type: "chip", label: "GPS: Inside site radius" },
            { type: "chip", label: "08:02 - Asia/Muscat (GST +4)" },
            { type: "button", label: "Clock In" },
            { type: "caption", label: "AI face match 98% - verified" },
          ] } },
          { type: "note", text: "If your GPS is outside the site radius, the clock-in is blocked. If the AI match confidence is low, the record is sent to the Admin's AI Review Queue for manual approval — you may be asked to try again." },
        ],
      },
      {
        key: "staff-desk",
        title: "4. Leaving the Desk",
        icon: DoorOpen,
        blocks: [
          { type: "p", text: "Some roles are allowed to step away from their desk / site during duty. This is controlled by the \"Allow Leave Desk\" setting on your employee profile." },
          {
            type: "steps",
            items: [
              "If your profile has Allow Leave Desk enabled, you can record a desk-leave and a return during your shift.",
              "If the setting is off, leaving the site during duty is restricted and may be flagged.",
            ],
          },
          { type: "mockup", screen: { screen: "On Duty", elements: [
            { type: "chip", label: "On duty since 08:02" },
            { type: "button", label: "Leave Desk" },
            { type: "button", label: "Return to Desk", variant: "outline" },
            { type: "caption", label: "Only if Allow Leave Desk is enabled" },
          ] } },
          { type: "note", text: "Need this enabled? Ask your administrator to switch on \"Allow Leave Desk\" on your employee profile." },
        ],
      },
      {
        key: "staff-ot",
        title: "5. Overtime Requests",
        icon: Timer,
        blocks: [
          { type: "p", text: "When you work beyond your scheduled duty hours, you must explicitly submit an Overtime request for approval. Overtime is never counted automatically — you enter the duration and a reason, and a manager approves or rejects it." },
          {
            type: "steps",
            items: [
              "Open the app and go to Overtime, then tap New Request.",
              "Select the Date the overtime was worked.",
              "Enter the number of Hours (duration) of overtime.",
              "Type a short Reason explaining why the overtime was required.",
              "Tap Submit Request — the status shows as Pending.",
              "Your manager reviews the request and Approves or Rejects it; the updated status appears in the app.",
              "Once approved, the overtime is automatically included in the Overtime Report in the Admin Portal.",
            ],
          },
          { type: "mockup", screen: { screen: "New Overtime Request", elements: [
            { type: "field", label: "Date", value: "14/09/2026" },
            { type: "field", label: "Hours", value: "2.5" },
            { type: "field", label: "Reason", value: "Month-end stock count" },
            { type: "button", label: "Submit Request" },
            { type: "chip", label: "Status: Pending approval", tone: "amber" },
          ] } },
          { type: "note", text: "Overtime must be requested and approved — hours are never auto-counted. Pending requests do not appear on reports until a manager approves them." },
        ],
      },
      {
        key: "staff-pin-reset",
        title: "6. Self-Service PIN Reset",
        icon: KeyRound,
        blocks: [
          { type: "p", text: "Staff who forget their PIN no longer need to wait for an administrator. Using AI Face Verification, a staff member can securely reset their own 4-digit PIN directly in the app." },
          {
            type: "steps",
            items: [
              "On the Sign In screen, tap \"Forgot PIN?\".",
              "Enter your Staff ID.",
              "Capture a live selfie when prompted.",
              "Claude AI matches your selfie against your enrolled reference face.",
              "On a confident match, choose and confirm a new 4-digit PIN.",
              "Sign in immediately with your new PIN.",
            ],
          },
          { type: "mockup", screen: { screen: "Reset PIN", elements: [
            { type: "camera", label: "Verify your identity" },
            { type: "pin", label: "New 4-Digit PIN" },
            { type: "button", label: "Reset PIN" },
            { type: "caption", label: "AI face match required" },
          ] } },
          { type: "note", text: "Manager visibility: every self-service PIN reset automatically triggers an instant Push notification and an Email alert to administrators/managers, so account changes are always visible and fully auditable." },
        ],
      },
      {
        key: "staff-biometric-login",
        title: "7. Biometric Security — Face ID & Fingerprint Login",
        icon: Fingerprint,
        blocks: [
          { type: "p", text: "Staff can now sign in using their device's built-in Face ID or Fingerprint, in addition to their Staff ID and PIN. This makes daily sign-ins faster and adds a strong layer of security for shared or field devices." },
          {
            type: "steps",
            items: [
              "After signing in with your PIN, enable Biometric Login when prompted (or later from Settings).",
              "Approve your device's Face ID / Fingerprint permission.",
              "On future logins, simply authenticate with your face or fingerprint.",
              "Your PIN always remains available as a fallback if biometrics are unavailable.",
            ],
          },
          { type: "mockup", screen: { screen: "Biometric Login", elements: [
            { type: "chip", label: "Face ID enabled" },
            { type: "chip", label: "Fingerprint enabled" },
            { type: "button", label: "Sign in with Biometrics" },
            { type: "caption", label: "PIN still works as backup" },
          ] } },
          { type: "note", text: "Biometric data never leaves the device — it is handled by the phone's secure hardware. This protects company accounts on field devices even if a PIN is observed or shared." },
        ],
      },
      {
        key: "staff-instant-clock",
        title: "8. Instant Clock In — Fast Tracking",
        icon: Zap,
        blocks: [
          { type: "p", text: "Instant Clock In makes check-ins feel immediate. Rather than waiting for the AI face match to finish, the app records the clock-in the moment the selfie and GPS are captured, then completes the AI verification in the background — ideal for busy shift starts." },
          {
            type: "steps",
            items: [
              "Tap Clock In as usual — your selfie and GPS are captured instantly.",
              "Your clock-in time is recorded immediately (Asia / Muscat, GST +4), so staff are never held up.",
              "Claude AI verifies the face match in the background.",
              "If the match is confident, the record is confirmed automatically.",
              "If confidence is low, the record is routed to the Admin AI Review Queue — exactly as before.",
            ],
          },
          { type: "mockup", screen: { screen: "Instant Clock In", elements: [
            { type: "chip", label: "08:02 recorded instantly" },
            { type: "chip", label: "AI verifying in background…" },
            { type: "button", label: "Clock In" },
            { type: "caption", label: "Faster check-ins for busy sites" },
          ] } },
          { type: "note", text: "Instant Clock In speeds up check-ins without weakening security: every record is still AI-verified, and any low-confidence match is still sent to an administrator for review." },
        ],
      },
    ],
  },
  {
    key: "admin",
    audience: "Administrators — Web Portal",
    icon: Monitor,
    sections: [
      {
        key: "admin-login",
        title: "9. Signing In to the Admin Portal",
        icon: LogIn,
        blocks: [
          { type: "p", text: "Administrators, HR managers and Super Admins manage the whole system from the web portal. Access to each area depends on the role and permissions granted to your account." },
          {
            type: "steps",
            items: [
              "Open the Admin Portal at smrtin.net and go to the Sign In page.",
              "Enter your email address and password.",
              "Click Sign In to reach your Dashboard.",
            ],
          },
          { type: "figure", src: "/manual/login.png", caption: "The Admin Portal sign-in screen." },
        ],
      },
      {
        key: "admin-dashboard",
        title: "10. Dashboard Overview",
        icon: LayoutDashboard,
        blocks: [
          { type: "p", text: "The Dashboard gives a live command-center view of your workforce for the current day (all times shown in Asia / Muscat)." },
          {
            type: "steps",
            items: [
              "Top KPI cards show Employees (used / quota), Active Sites, Present Today and Pending AI Reviews.",
              "Attendance Trend charts clock-ins over the last 7 days.",
              "Status Breakdown and Employees-per-Site charts summarise the workforce distribution.",
              "AI Face-Match Audits shows the outcome of Claude verification checks.",
            ],
          },
          { type: "figure", src: "/manual/dashboard.png", caption: "Dashboard with KPI cards and attendance charts." },
        ],
      },
      {
        key: "admin-sites",
        title: "11. Adding Sites (Google Maps)",
        icon: MapPin,
        blocks: [
          { type: "p", text: "A Site is a geofenced location. Staff can only clock in when they are physically inside a site's radius." },
          {
            type: "steps",
            items: [
              "Open the Sites page and click Add Site.",
              "The map auto-centers on your current GPS location and drops a pin.",
              "Search for an address, click on the map, or drag the pin to set the exact location. Use the Map / Hybrid toggle for an aerial view.",
              "Give the site a Name. Latitude, Longitude and the formatted address are filled from the map (all editable).",
              "Set the Geofence Radius in meters (how close staff must be to clock in).",
              "Click Create Site to save.",
            ],
          },
          { type: "figure", src: "/manual/site_form.png", caption: "Add Site — pick the location on Google Maps and set the geofence radius." },
          { type: "figure", src: "/manual/sites.png", caption: "The Sites list. Super Admins can assign specific admins to each site." },
          { type: "note", text: "Data isolation: standard admins see only the sites they created or were assigned to. Super Admins see and manage every site." },
        ],
      },
      {
        key: "admin-employees",
        title: "12. Managing Employees & Quotas",
        icon: Users,
        blocks: [
          { type: "p", text: "Employees are your staff members. Each admin manages their own employees within a quota set by the Super Admin." },
          {
            type: "steps",
            items: [
              "Open the Employees page and click Add Employee.",
              "Enter Staff ID, Full Name and a 4-digit PIN (the staff member uses these to log into the mobile app).",
              "Optionally set Role Title, Email and the Assigned Site.",
              "Set Duty Start / Duty End (Asia / Muscat) and the Grace period in minutes.",
              "Choose Weekly Off Days — staff are not flagged Late or Absent on these days.",
              "Toggle Allow Leave Desk if the employee may step away during duty, then click Add Employee.",
              "To add many staff at once, use Bulk Upload — download the Template first to get the correct columns.",
            ],
          },
          { type: "figure", src: "/manual/employee_form.png", caption: "Add Employee — duty hours, grace, off-days and Allow Leave Desk." },
          { type: "p", text: "Quotas & user roles are managed by the Super Admin from User Management." },
          {
            type: "steps",
            items: [
              "Open User Management and click Add Admin to create an administrator account.",
              "Choose the role (Admin / HR Manager), grant module permissions, and set can-edit / can-delete rights.",
              "Set the employee Quota for that admin (blank = unlimited). Each row shows Quota used / total.",
              "Reset a staff PIN or an admin password from the row actions here.",
            ],
          },
          { type: "figure", src: "/manual/users.png", caption: "User Management — admins, roles, quotas and PIN / password resets." },
          { type: "note", text: "An admin who reaches their employee quota sees: \u201cEmployee limit reached. Please contact your Super Admin.\u201d" },
        ],
      },
      {
        key: "admin-reviews",
        title: "13. Reviewing AI Flags",
        icon: ClipboardCheck,
        blocks: [
          { type: "p", text: "When the Claude AI face-match confidence is low, the clock-in is held in the AI Review Queue instead of being auto-approved." },
          {
            type: "steps",
            items: [
              "Open the AI Review Queue page.",
              "For each flagged clock-in, compare the live selfie against the enrolled reference photo and the AI confidence score.",
              "Click Approve to accept the attendance, or Reject to discard it.",
              "Approved records immediately count towards attendance; rejected ones do not.",
            ],
          },
          { type: "figure", src: "/manual/reviews.png", caption: "AI Review Queue — approve or reject low-confidence face matches." },
        ],
      },
      {
        key: "admin-reports",
        title: "14. Generating Reports",
        icon: FileBarChart,
        blocks: [
          { type: "p", text: "The Reports page produces branded, exportable reports. Access requires the Reports permission (Super Admins always have it)." },
          {
            type: "steps",
            items: [
              "Open the Reports page and choose a tab: Late Attendance, Attendance Report, or Overtime Report.",
              "Filter by Employee, Site and a date range (defaults to the last 30 days). Dates are shown as dd/mm/yyyy.",
              "Review the results in the on-screen table.",
              "Click PDF to download a branded PDF, or Excel to download a spreadsheet.",
            ],
          },
          { type: "figure", src: "/manual/reports.png", caption: "Reports — filter by employee, site and date range, then export to PDF or Excel." },
          { type: "note", text: "Late = a clock-in after the shift start time. All report times are calculated in the Asia / Muscat timezone (GST +4)." },
        ],
      },
      {
        key: "admin-businesses",
        title: "15. Multi-Company (Businesses) Setup",
        icon: Building2,
        blocks: [
          { type: "p", text: "The Businesses module lets a Super Admin run several companies from a single Smart-In account. Each employee, admin and their data can be grouped under a distinct business, and the company name flows through to the staff mobile app header." },
          {
            type: "steps",
            items: [
              "Open the Businesses page (requires the Businesses permission; Super Admins always have it) and click Add Business.",
              "Enter the Company / Business Name and save — it appears instantly in the businesses list.",
              "When creating or editing an Employee, pick the Business from the dropdown so the employee is grouped correctly.",
              "For Admin accounts, assign a Business on the User Management screen; that admin is then scoped to their company's data only.",
              "If an admin already has an assigned business, the Business dropdown is hidden during employee creation and the company is auto-applied. Super Admins can always choose any business.",
              "Use the Business column and filter on the User Management table to review or narrow accounts by company.",
            ],
          },
          { type: "note", text: "Mobile header sync: each staff member's assigned company name is surfaced in the mobile app (via the account's business_name), so field staff always see which organisation they are clocking in for." },
        ],
      },
      {
        key: "admin-desk-sessions",
        title: "16. Desk Session History",
        icon: Coffee,
        blocks: [
          { type: "p", text: "Desk Sessions record every time a permitted staff member leaves their desk / site and returns during a shift (for example for Prayer, Lunch or a Meeting). Sessions are logged from the mobile app and appear on the dedicated Desk Sessions page in the portal." },
          {
            type: "steps",
            items: [
              "Open the Desk Sessions link in the sidebar (under Attendance Logs).",
              "Each row shows the Staff member, the Reason / Category, the Site, the Start and End times (Asia / Muscat), and the total Duration.",
              "A session still in progress shows as Active; a completed one shows as Closed.",
              "The Outcome column shows the work result the staff selects on returning — Success or Unsuccess.",
              "Filter the list by Staff Name / ID, Date, or Category to find specific sessions, and use Reset to clear all filters.",
              "To preview the table with sample data, use Create Test Session on the Notification Settings page.",
            ],
          },
          { type: "note", text: "The Leave Desk feature only appears in the mobile app for employees who have the \u201cAllow Leave Desk\u201d permission enabled on their profile." },
        ],
      },
      {
        key: "admin-pin-recovery",
        title: "17. AI PIN Recovery & Manager Alerts",
        icon: KeyRound,
        blocks: [
          { type: "p", text: "Staff can securely reset a forgotten PIN themselves using AI Face Verification — no administrator action required — while every reset stays fully auditable for managers." },
          {
            type: "steps",
            items: [
              "The staff member taps \u201cForgot PIN?\u201d on the mobile Sign In screen and enters their Staff ID.",
              "They capture a live selfie, which the Claude AI engine (gpt-4o-mini) compares against their enrolled master reference photo.",
              "On a confident match, the staff member sets and confirms a new 4-digit PIN and signs in immediately.",
              "A brute-force guard temporarily locks the flow after repeated failed attempts to keep accounts safe.",
              "Every successful reset instantly triggers a Push notification and an Email alert to administrators / managers.",
            ],
          },
          { type: "p", text: "Administrator prerequisite: AI PIN Recovery relies on a master reference photo for each employee." },
          {
            type: "steps",
            items: [
              "Open the Employees page and edit the staff member.",
              "Upload or update their Master Photo (used for AI face verification).",
              "Save — the employee can now use self-service PIN recovery. You can still reset any PIN manually from User Management at any time.",
            ],
          },
          { type: "note", text: "Because each reset notifies managers by push and email, account changes are always visible — self-service convenience without losing oversight." },
        ],
      },
      {
        key: "admin-notifications",
        title: "18. Automated Notifications",
        icon: Bell,
        blocks: [
          { type: "p", text: "Smart-In keeps managers informed automatically through email and push notifications. Alerts are generated whether attendance is recorded through the portal or synced directly from the mobile app." },
          {
            type: "steps",
            items: [
              "Clock-In / Clock-Out alerts: every time a staff member clocks in or out, the assigned site's managers receive an email; late clock-ins include the exact Minutes Late.",
              "Late-arrival alerts: if an employee has not clocked in within your chosen threshold after their shift start (plus grace), the broadcast list is emailed automatically.",
              "PIN-reset alerts: every self-service AI PIN recovery notifies administrators by push and email.",
              "Push notifications: staff receive instant confirmations for clock-in, clock-out and key account events on their device.",
            ],
          },
          { type: "p", text: "Configure recipients and thresholds from Notification Settings." },
          {
            type: "steps",
            items: [
              "Open Notification Settings (Super Admin).",
              "Toggle Late Attendance Alerts on, and pick the Alert Threshold (5 / 10 / 20 / 30 minutes).",
              "Add one or more Broadcast Recipients (name + email) to receive alerts.",
              "Add per-site Manager Emails on each Site so clock-in / clock-out alerts reach the right people.",
              "Use Send Test Alert to confirm delivery is working.",
            ],
          },
          { type: "note", text: "Email delivery is powered by Resend and sends from smrtin.net. Alerts respect each employee's off-days and are timed to the Asia / Muscat timezone (GST +4)." },
        ],
      },
    ],
  },
];

// ---- About / product summary (single source for the manual front page) ----
const ABOUT = {
  short:
    "Smart-In is an AI-powered workforce attendance system that verifies every clock-in with facial recognition and GPS geofencing — accurate, tamper-proof and effortless.",
  long:
    "Smart-In pairs a lightweight staff mobile app with a powerful web Admin Portal to give organisations complete, trustworthy control over attendance. Staff clock in and out with a quick selfie that Claude AI matches against their enrolled reference photo at 98%+ confidence, while precise GPS geofencing confirms they are physically on-site. Administrators get a live dashboard, an AI Review Queue for low-confidence matches, granular role-based permissions, multi-site and multi-user management with quotas, and branded PDF/Excel reports. With reliable offline GPS support and automated email notifications to site managers, Smart-In keeps attendance honest and every management layer informed — anywhere, anytime.",
  features: [
    "AI Face Verification powered by Claude (98%+ match confidence)",
    "Precise GPS geofencing for every site",
    "Secure biometric / face enrolment on first sign-in",
    "Role-based access: Super Admin, Admin, HR Manager and Staff",
    "Multi-site and multi-user management with per-admin quotas",
    "Multi-Company (Businesses) support — run several companies from one account",
    "Desk Session History — leave-desk / return tracking with outcomes",
    "Self-service AI PIN Recovery with automatic manager alerts",
    "Attendance logs, AI Review Queue and Overtime approval workflow",
    "Branded PDF and Excel reports with employee, site and date filters",
    "Offline Support (GPS only) — clock-ins are captured and synced when back online",
    "Automated clock-in/out, late-arrival and PIN-reset notifications (email + push)",
    "Site Manager Email Notifications with a live site attendance summary",
  ],
  highlights: [
    "98%+ AI Face Verification",
    "Precise GPS Geofencing",
    "Multi-Company Management",
    "Automated Manager Alerts",
  ],
};

const IMG_CACHE = {};
async function loadImage(src) {
  if (IMG_CACHE[src]) return IMG_CACHE[src];
  const res = await fetch(src);
  const blob = await res.blob();
  const bmp = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  canvas.getContext("2d").drawImage(bmp, 0, 0);
  const out = { dataUrl: canvas.toDataURL("image/jpeg", 0.85), w: bmp.width, h: bmp.height };
  IMG_CACHE[src] = out;
  return out;
}

// ---- On-screen phone mockup (illustrative — not a real screenshot) ----
function MockEl({ el }) {
  if (el.type === "field")
    return (
      <div>
        <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{el.label}</p>
        <div className="h-8 rounded-lg border border-slate-200 bg-slate-50 px-2.5 flex items-center text-xs text-slate-600">
          {el.value || ""}
        </div>
      </div>
    );
  if (el.type === "pin")
    return (
      <div>
        <p className="text-[9px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{el.label}</p>
        <div className="flex gap-2">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className="h-8 w-8 rounded-lg border border-slate-200 bg-slate-50 grid place-items-center text-slate-500 text-lg leading-none">•</span>
          ))}
        </div>
      </div>
    );
  if (el.type === "camera")
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 aspect-[4/3] grid place-items-center text-center px-3">
        <div className="space-y-1.5">
          <ScanFace className="h-7 w-7 text-slate-400 mx-auto" />
          <p className="text-[10px] text-slate-400 leading-tight">{el.label}</p>
        </div>
      </div>
    );
  if (el.type === "chip") {
    const tone = el.tone === "amber" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
    const dot = el.tone === "amber" ? "bg-amber-500" : "bg-emerald-500";
    return (
      <div className={`inline-flex items-center gap-1.5 rounded-full ${tone} text-[10px] font-semibold px-2.5 py-1`}>
        <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
        {el.label}
      </div>
    );
  }
  if (el.type === "button")
    return el.variant === "outline" ? (
      <div className="h-9 rounded-lg border border-slate-300 bg-white grid place-items-center text-xs font-semibold text-slate-600">{el.label}</div>
    ) : (
      <div className="h-9 rounded-lg bg-indigo-600 grid place-items-center text-xs font-semibold text-white shadow-sm">{el.label}</div>
    );
  if (el.type === "caption")
    return <p className="text-[10px] text-slate-400 text-center italic leading-tight">{el.label}</p>;
  return null;
}

function PhoneMock({ screen }) {
  return (
    <div className="flex justify-center py-2">
      <div className="w-[248px] rounded-[2rem] border-[6px] border-slate-900 bg-slate-900 shadow-xl shadow-slate-900/20">
        <div className="rounded-[1.55rem] overflow-hidden bg-white">
          <div className="relative bg-slate-900 py-2.5">
            <span className="absolute left-1/2 -translate-x-1/2 top-1.5 h-1.5 w-14 rounded-full bg-slate-700" />
            <p className="text-center text-[11px] font-semibold text-white mt-1.5">{screen.screen}</p>
          </div>
          <div className="p-4 space-y-3 min-h-[240px] flex flex-col">
            {screen.elements.map((el, i) => (
              <MockEl key={i} el={el} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


export default function HelpManual() {
  const [exporting, setExporting] = useState(false);

  const exportPDF = async () => {
    setExporting(true);
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const PW = doc.internal.pageSize.getWidth();
      const PH = doc.internal.pageSize.getHeight();
      const M = 48;
      const CW = PW - M * 2;
      let y = M;
      let page = 1;

      // MyGate theme palette (RGB) — yellow primary, coral highlights.
      const YELLOW = [255, 196, 0];       // primary fills / banners
      const INK = [26, 21, 0];            // near-black text on yellow
      const CORAL = [255, 90, 77];        // accent fills / bullets
      const CORAL_INK = [226, 66, 52];    // coral text on white
      const CORAL_TINT = [255, 236, 234]; // soft coral surface
      const CORAL_RING = [255, 214, 209]; // coral borders

      const logo = await loadImage("/logo-lg.webp").catch(() => null);

      const footer = () => {
        doc.setDrawColor(226, 232, 240);
        doc.line(M, PH - 34, PW - M, PH - 34);
        doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(148, 163, 184);
        doc.text("Smart-In User Manual", M, PH - 20);
        doc.text(`Page ${page}`, PW - M, PH - 20, { align: "right" });
      };
      const newPage = () => {
        footer();
        doc.addPage();
        page += 1;
        y = M;
      };
      const ensure = (h) => {
        if (y + h > PH - 48) newPage();
      };

      const elemAdvance = (el) => {
        switch (el.type) {
          case "field":
          case "pin": return 40;
          case "camera": return 86;
          case "chip": return 22;
          case "button": return 30;
          case "caption": return 16;
          default: return 0;
        }
      };
      const drawMockup = (screen) => {
        const w = 190;
        const x = M + (CW - w) / 2;
        const bodyH = screen.elements.reduce((s, el) => s + elemAdvance(el), 0);
        const H = 38 + bodyH + 12;
        ensure(H + 12);
        const yTop = y;
        doc.setFillColor(15, 23, 42);
        doc.roundedRect(x, yTop, w, H, 18, 18, "F");
        doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(255, 255, 255);
        doc.text(screen.screen, x + w / 2, yTop + 16, { align: "center" });
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x + 6, yTop + 26, w - 12, H - 32, 10, 10, "F");
        const innerX = x + 16;
        const innerW = w - 32;
        let cy = yTop + 38;
        for (const el of screen.elements) {
          if (el.type === "field") {
            doc.setFont("helvetica", "bold").setFontSize(6.5).setTextColor(148, 163, 184);
            doc.text(String(el.label).toUpperCase(), innerX, cy + 6);
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(226, 232, 240);
            doc.roundedRect(innerX, cy + 10, innerW, 18, 4, 4, "FD");
            doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(71, 85, 105);
            doc.text(String(el.value || ""), innerX + 6, cy + 22);
            cy += 40;
          } else if (el.type === "pin") {
            doc.setFont("helvetica", "bold").setFontSize(6.5).setTextColor(148, 163, 184);
            doc.text(String(el.label).toUpperCase(), innerX, cy + 6);
            let px = innerX;
            for (let i = 0; i < 4; i++) {
              doc.setFillColor(248, 250, 252);
              doc.setDrawColor(226, 232, 240);
              doc.roundedRect(px, cy + 10, 18, 18, 4, 4, "FD");
              doc.setFillColor(100, 116, 139);
              doc.circle(px + 9, cy + 19, 1.6, "F");
              px += 24;
            }
            cy += 40;
          } else if (el.type === "camera") {
            doc.setFillColor(248, 250, 252);
            doc.setDrawColor(203, 213, 225);
            doc.setLineDashPattern([2, 2], 0);
            doc.roundedRect(innerX, cy + 4, innerW, 74, 6, 6, "FD");
            doc.setLineDashPattern([], 0);
            doc.setFillColor(226, 232, 240);
            doc.circle(innerX + innerW / 2, cy + 30, 10, "F");
            doc.setFont("helvetica", "normal").setFontSize(7.5).setTextColor(148, 163, 184);
            const cl = doc.splitTextToSize(String(el.label), innerW - 8);
            doc.text(cl, innerX + innerW / 2, cy + 54, { align: "center" });
            cy += 86;
          } else if (el.type === "chip") {
            const amber = el.tone === "amber";
            doc.setFont("helvetica", "bold").setFontSize(7.5);
            const tw = doc.getTextWidth(String(el.label));
            const pw2 = Math.min(tw + 22, innerW);
            doc.setFillColor(...(amber ? [254, 243, 199] : [209, 250, 229]));
            doc.roundedRect(innerX, cy + 2, pw2, 15, 7.5, 7.5, "F");
            doc.setFillColor(...(amber ? [217, 119, 6] : [5, 150, 105]));
            doc.circle(innerX + 8, cy + 9.5, 2, "F");
            doc.setTextColor(...(amber ? [146, 64, 14] : [4, 120, 87]));
            doc.text(String(el.label), innerX + 14, cy + 12);
            cy += 22;
          } else if (el.type === "button") {
            if (el.variant === "outline") {
              doc.setDrawColor(203, 213, 225);
              doc.setFillColor(255, 255, 255);
              doc.roundedRect(innerX, cy + 4, innerW, 22, 6, 6, "FD");
              doc.setTextColor(71, 85, 105);
            } else {
              doc.setFillColor(...YELLOW);
              doc.roundedRect(innerX, cy + 4, innerW, 22, 6, 6, "F");
              doc.setTextColor(...INK);
            }
            doc.setFont("helvetica", "bold").setFontSize(8.5);
            doc.text(String(el.label), innerX + innerW / 2, cy + 18, { align: "center" });
            cy += 30;
          } else if (el.type === "caption") {
            doc.setFont("helvetica", "italic").setFontSize(7.5).setTextColor(148, 163, 184);
            const cl = doc.splitTextToSize(String(el.label), innerW);
            doc.text(cl, innerX + innerW / 2, cy + 8, { align: "center" });
            cy += 16;
          }
        }
        y = yTop + H + 12;
      };

      // ---- About front page ----
      const drawAbout = () => {
        if (logo) {
          const lw = 74;
          const lh = (logo.h / logo.w) * lw;
          doc.addImage(logo.dataUrl, "JPEG", (PW - lw) / 2, y, lw, lh);
          y += lh + 18;
        }
        doc.setFont("helvetica", "bold").setFontSize(21).setTextColor(15, 23, 42);
        const hd = doc.splitTextToSize("About Smart-In AI Attendance System", CW);
        doc.text(hd, PW / 2, y + 6, { align: "center" });
        y += hd.length * 24 + 4;
        doc.setDrawColor(...CORAL).setLineWidth(2);
        doc.line(PW / 2 - 44, y, PW / 2 + 44, y);
        doc.setLineWidth(1);
        y += 22;
        doc.setFont("helvetica", "bold").setFontSize(11.5).setTextColor(...CORAL_INK);
        const sd = doc.splitTextToSize(ABOUT.short, CW);
        doc.text(sd, M, y);
        y += sd.length * 15 + 12;
        doc.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(51, 65, 85);
        const ld = doc.splitTextToSize(ABOUT.long, CW);
        doc.text(ld, M, y);
        y += ld.length * 14 + 18;
        // Key Highlights box
        const hi = ABOUT.highlights;
        const rows = Math.ceil(hi.length / 2);
        const boxH = 44 + rows * 24 + 6;
        ensure(boxH + 4);
        doc.setFillColor(...CORAL_TINT);
        doc.setDrawColor(...CORAL_RING);
        doc.roundedRect(M, y, CW, boxH, 8, 8, "FD");
        doc.setFont("helvetica", "bold").setFontSize(11.5).setTextColor(...CORAL_INK);
        doc.text("Key Highlights", M + 16, y + 24);
        const colW = (CW - 32) / 2;
        hi.forEach((it, i) => {
          const col = i % 2;
          const row = Math.floor(i / 2);
          const cx = M + 16 + col * colW;
          const cyH = y + 44 + row * 24;
          doc.setFillColor(...CORAL);
          doc.circle(cx + 5, cyH - 3, 5.5, "F");
          doc.setDrawColor(255, 255, 255).setLineWidth(1.4);
          doc.line(cx + 2.4, cyH - 3.2, cx + 4.3, cyH - 1.2);
          doc.line(cx + 4.3, cyH - 1.2, cx + 7.6, cyH - 5.4);
          doc.setLineWidth(1);
          doc.setFont("helvetica", "bold").setFontSize(9.5).setTextColor(30, 27, 75);
          doc.text(it, cx + 18, cyH);
        });
        y += boxH + 20;
        // Core Features
        ensure(30);
        doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(15, 23, 42);
        doc.text("Core Features", M, y);
        y += 8;
        doc.setDrawColor(226, 232, 240);
        doc.line(M, y, M + CW, y);
        y += 16;
        doc.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(51, 65, 85);
        ABOUT.features.forEach((f) => {
          const lines = doc.splitTextToSize(f, CW - 16);
          ensure(lines.length * 14 + 2);
          doc.setFillColor(...CORAL);
          doc.circle(M + 3, y - 3, 1.8, "F");
          doc.text(lines, M + 12, y);
          y += lines.length * 14 + 5;
        });
      };

      // ---- Cover page ----
      doc.setFillColor(15, 23, 42);
      doc.rect(0, 0, PW, PH, "F");
      if (logo) {
        const lw = 92;
        const lh = (logo.h / logo.w) * lw;
        doc.addImage(logo.dataUrl, "JPEG", (PW - lw) / 2, 170, lw, lh);
      }
      doc.setFont("helvetica", "bold").setFontSize(34).setTextColor(255, 255, 255);
      doc.text("Smart-In", PW / 2, 320, { align: "center" });
      doc.setFontSize(20).setTextColor(...YELLOW);
      doc.text("User Manual", PW / 2, 352, { align: "center" });
      doc.setFont("helvetica", "normal").setFontSize(11).setTextColor(148, 163, 184);
      doc.text("Attendance App & Admin Portal · smrtin.net", PW / 2, 380, { align: "center" });
      doc.setDrawColor(...YELLOW);
      doc.setLineWidth(2);
      doc.line(PW / 2 - 40, 398, PW / 2 + 40, 398);
      doc.setLineWidth(1);
      doc.setFontSize(10).setTextColor(148, 163, 184);
      doc.text(`Generated: ${muscatNow()} (GST +4)`, PW / 2, 470, { align: "center" });
      doc.text("Timezone: Asia / Muscat", PW / 2, 488, { align: "center" });
      doc.setFontSize(9).setTextColor(100, 116, 139);
      doc.text("Confidential — for authorized staff only", PW / 2, PH - 60, { align: "center" });

      // ---- About / front page ----
      doc.addPage();
      page += 1;
      y = M;
      drawAbout();

      // ---- Content ----
      newPage();

      for (const group of MANUAL) {
        // Group banner
        ensure(50);
        doc.setFillColor(...YELLOW);
        doc.roundedRect(M, y, CW, 30, 5, 5, "F");
        doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(...INK);
        doc.text(group.audience, M + 14, y + 20);
        y += 46;

        for (const sec of group.sections) {
          ensure(40);
          doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(15, 23, 42);
          let title = sec.title;
          if (sec.badge) title += `   (${sec.badge})`;
          doc.text(title, M, y);
          y += 8;
          doc.setDrawColor(226, 232, 240);
          doc.line(M, y, M + CW, y);
          y += 16;

          for (const b of sec.blocks) {
            if (b.type === "p") {
              doc.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(51, 65, 85);
              const lines = doc.splitTextToSize(b.text, CW);
              ensure(lines.length * 14);
              doc.text(lines, M, y);
              y += lines.length * 14 + 8;
            } else if (b.type === "steps") {
              doc.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(51, 65, 85);
              b.items.forEach((it, i) => {
                const lines = doc.splitTextToSize(`${i + 1}.  ${it}`, CW - 16);
                ensure(lines.length * 14 + 2);
                doc.text(lines, M + 8, y);
                y += lines.length * 14 + 4;
              });
              y += 6;
            } else if (b.type === "note") {
              doc.setFont("helvetica", "italic").setFontSize(9.5).setTextColor(120, 53, 15);
              const lines = doc.splitTextToSize(`Note:  ${b.text}`, CW - 20);
              const boxH = lines.length * 12 + 16;
              ensure(boxH);
              doc.setFillColor(255, 251, 235);
              doc.setDrawColor(253, 230, 138);
              doc.roundedRect(M, y, CW, boxH, 4, 4, "FD");
              doc.text(lines, M + 10, y + 14);
              y += boxH + 10;
            } else if (b.type === "mockup") {
              drawMockup(b.screen);
            } else if (b.type === "figure") {
              const img = await loadImage(b.src).catch(() => null);
              if (img) {
                const iw = CW;
                const ih = (img.h / img.w) * iw;
                ensure(ih + 22);
                doc.setDrawColor(226, 232, 240);
                doc.addImage(img.dataUrl, "JPEG", M, y, iw, ih);
                doc.rect(M, y, iw, ih);
                y += ih + 6;
                doc.setFont("helvetica", "italic").setFontSize(8.5).setTextColor(100, 116, 139);
                const cap = doc.splitTextToSize(b.caption, CW);
                doc.text(cap, M, y + 8);
                y += cap.length * 11 + 12;
              }
            }
          }
          y += 8;
        }
      }
      footer();
      doc.save(`Smart-In_User_Manual_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("Manual PDF downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate the PDF");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl" data-testid="help-manual-page">
      {/* Header / download */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <img src="/logo.webp" alt="Smart-In" className="h-14 w-14 rounded-2xl object-cover bg-white ring-1 ring-slate-200" />
        <div className="flex-1">
          <h2 className="font-head text-2xl font-extrabold text-slate-900 tracking-tight">Smart-In User Manual</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Step-by-step guide for the Attendance App and the Admin Portal.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Generated {muscatDate()} · Timezone Asia / Muscat (GST +4) · smrtin.net
          </p>
        </div>
        <button
          onClick={exportPDF}
          disabled={exporting}
          data-testid="download-manual-pdf-btn"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-60 transition shadow-lg shadow-indigo-600/20"
        >
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
          Download PDF
        </button>
      </div>

      <div className="flex items-start gap-3 rounded-xl border border-indigo-100 bg-indigo-50/60 px-4 py-3 text-sm text-indigo-900" data-testid="help-manual-scope-note">
        <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <p>This manual is visible and downloadable by the Super Admin only. Admin Portal screens are captured live from the current portal; Staff app screens are illustrative mockups.</p>
      </div>

      {/* About / product summary — matches the PDF front page */}
      <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8" data-testid="manual-about">
        <div className="flex flex-col items-center text-center">
          <img src="/logo-lg.webp" alt="MRP Smart-In" className="h-16 w-auto object-contain" onError={(e) => { e.currentTarget.src = "/logo.webp"; }} />
          <h3 className="mt-4 font-head text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">About Smart-In AI Attendance System</h3>
          <span className="mt-3 block h-1 w-20 rounded-full bg-indigo-600" />
        </div>
        <p className="mt-6 text-base font-semibold text-indigo-700 leading-relaxed">{ABOUT.short}</p>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">{ABOUT.long}</p>

        <div className="mt-6 rounded-2xl bg-indigo-50 border border-indigo-100 p-5" data-testid="manual-key-highlights">
          <p className="text-sm font-bold uppercase tracking-wide text-indigo-800">Key Highlights</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {ABOUT.highlights.map((h) => (
              <div key={h} className="flex items-center gap-2.5">
                <span className="grid place-items-center h-6 w-6 rounded-full bg-indigo-600 text-white flex-shrink-0">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <span className="text-sm font-semibold text-slate-800">{h}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <h4 className="font-head text-lg font-bold text-slate-900">Core Features</h4>
          <div className="mt-1 h-px bg-slate-200" />
          <ul className="mt-3 space-y-2">
            {ABOUT.features.map((f) => (
              <li key={f} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-indigo-600 flex-shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* On-screen readable manual */}
      {MANUAL.map((group) => {
        const GIcon = group.icon;
        return (
          <div key={group.key} className="space-y-4" data-testid={`manual-group-${group.key}`}>
            <div className="flex items-center gap-3 rounded-xl bg-slate-900 text-white px-5 py-3">
              <GIcon className="h-5 w-5 text-indigo-300" />
              <h3 className="font-head text-lg font-bold tracking-tight">{group.audience}</h3>
            </div>

            {group.sections.map((sec) => {
              const SIcon = sec.icon;
              return (
                <section key={sec.key} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid={`manual-section-${sec.key}`}>
                  <div className="flex items-center gap-2.5 mb-4">
                    <span className="grid place-items-center h-9 w-9 rounded-lg bg-indigo-50 text-indigo-600">
                      <SIcon className="h-[18px] w-[18px]" />
                    </span>
                    <h4 className="font-head text-lg font-bold text-slate-900">{sec.title}</h4>
                    {sec.badge && (
                      <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-0.5">
                        {sec.badge}
                      </span>
                    )}
                  </div>

                  <div className="space-y-4">
                    {sec.blocks.map((b, i) => {
                      if (b.type === "p")
                        return <p key={i} className="text-sm text-slate-600 leading-relaxed">{b.text}</p>;
                      if (b.type === "steps")
                        return (
                          <ol key={i} className="space-y-2">
                            {b.items.map((it, j) => (
                              <li key={j} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                                <span className="flex-shrink-0 grid place-items-center h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold">
                                  {j + 1}
                                </span>
                                <span className="pt-0.5">{it}</span>
                              </li>
                            ))}
                          </ol>
                        );
                      if (b.type === "note")
                        return (
                          <div key={i} className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                            <Info className="h-4 w-4 mt-0.5 text-amber-600 flex-shrink-0" />
                            <p className="text-sm text-amber-800 leading-relaxed">{b.text}</p>
                          </div>
                        );
                      if (b.type === "mockup") return <PhoneMock key={i} screen={b.screen} />;
                      if (b.type === "figure")
                        return (
                          <figure key={i} className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                            <img src={b.src} alt={b.caption} loading="lazy" className="w-full block" />
                            <figcaption className="text-xs text-slate-500 px-4 py-2.5 border-t border-slate-100 bg-white">
                              {b.caption}
                            </figcaption>
                          </figure>
                        );
                      return null;
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
